import { loadIceServers } from './iceServers';

/**
 * pre190: Companion-Chat-Spiegel.
 *
 * Der Chat ist E2EE-P2P und lebt an der A/V-Hauptsitzung (`chatTransport`, DataChannel
 * `bamoa-chat`). Damit der Berater den Chat auch am Companion (Terminalserver, A/V-los)
 * lesen/schreiben kann, spiegelt die A/V-Session ihn über einen ZWEITEN, direkten
 * P2P-DataChannel (`bamoa-companion-chat`) an den Companion — der Server sieht dabei nur
 * das Signaling (SDP/ICE, huckepack auf remote:cmd/remote:state), NIE den Chat-Inhalt.
 *
 * Rollen: Der Companion ist der Offerer (er braucht den Spiegel), die A/V-Session der
 * Answerer. Das folgt demselben Muster wie die Aufzeichnungs-Übergabe (`bamoa-rec`).
 *
 * Nachrichtenformat auf dem Kanal:
 *   A/V → Companion:  { t:'snapshot', threads, names, status }
 *                     { t:'msg', peerId, name, msg }
 *                     { t:'status', peerId, st }
 *   Companion → A/V:  { t:'send', peerId, name, body }
 *                     { t:'open', peerId, name }
 */

export interface ChatSig { type: 'offer' | 'answer' | 'ice'; sdp?: any; candidate?: any }

/** A/V-Seite: beantwortet die Companion-Offer und hält den Spiegel-DataChannel. */
export class ChatMirrorHost {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  onOpen?: () => void;
  onClose?: () => void;

  constructor(
    private sendSignal: (s: ChatSig) => void,
    private onCommand: (msg: any) => void,
  ) {}

  get isOpen(): boolean { return this.dc?.readyState === 'open'; }

  send(obj: any): void {
    if (this.isOpen) { try { this.dc!.send(JSON.stringify(obj)); } catch { /* */ } }
  }

  async onSignal(s: ChatSig): Promise<void> {
    try {
      if (s.type === 'offer' && s.sdp) {
        this.reset();
        const iceServers = await loadIceServers();
        const pc = new RTCPeerConnection({ iceServers });
        this.pc = pc;
        pc.onicecandidate = (e) => { if (e.candidate) this.sendSignal({ type: 'ice', candidate: e.candidate.toJSON() }); };
        pc.ondatachannel = (e) => { this.dc = e.channel; this.bind(); };
        await pc.setRemoteDescription(s.sdp);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.sendSignal({ type: 'answer', sdp: pc.localDescription });
      } else if (s.type === 'ice' && s.candidate && this.pc) {
        try { await this.pc.addIceCandidate(s.candidate); } catch { /* */ }
      }
    } catch { /* */ }
  }

  private bind(): void {
    const dc = this.dc!;
    dc.onopen = () => { try { this.onOpen?.(); } catch { /* */ } };
    dc.onmessage = (ev) => {
      if (typeof ev.data === 'string') { try { this.onCommand(JSON.parse(ev.data)); } catch { /* */ } }
    };
    dc.onclose = () => { try { this.onClose?.(); } catch { /* */ } };
  }

  private reset(): void {
    try { this.dc?.close(); } catch { /* */ }
    try { this.pc?.close(); } catch { /* */ }
    this.dc = null; this.pc = null;
  }

  close(): void { this.reset(); }
}

/** Companion-Seite: baut die Offer auf und empfängt die Spiegel-Updates. */
export class ChatMirrorClient {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  onOpen?: () => void;
  onClose?: () => void;

  constructor(
    private sendSignal: (s: ChatSig) => void,
    private onUpdate: (msg: any) => void,
  ) {}

  get isOpen(): boolean { return this.dc?.readyState === 'open'; }

  send(obj: any): void {
    if (this.isOpen) { try { this.dc!.send(JSON.stringify(obj)); } catch { /* */ } }
  }

  async start(): Promise<void> {
    try {
      this.reset();
      const iceServers = await loadIceServers();
      const pc = new RTCPeerConnection({ iceServers });
      this.pc = pc;
      pc.onicecandidate = (e) => { if (e.candidate) this.sendSignal({ type: 'ice', candidate: e.candidate.toJSON() }); };
      const dc = pc.createDataChannel('bamoa-companion-chat', { ordered: true });
      this.dc = dc;
      dc.onopen = () => { try { this.onOpen?.(); } catch { /* */ } };
      dc.onmessage = (ev) => {
        if (typeof ev.data === 'string') { try { this.onUpdate(JSON.parse(ev.data)); } catch { /* */ } }
      };
      dc.onclose = () => { try { this.onClose?.(); } catch { /* */ } };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.sendSignal({ type: 'offer', sdp: pc.localDescription });
    } catch { /* */ }
  }

  async onSignal(s: ChatSig): Promise<void> {
    if (!this.pc) return;
    try {
      if (s.type === 'answer' && s.sdp) { await this.pc.setRemoteDescription(s.sdp); }
      else if (s.type === 'ice' && s.candidate) { await this.pc.addIceCandidate(s.candidate); }
    } catch { /* */ }
  }

  private reset(): void {
    try { this.dc?.close(); } catch { /* */ }
    try { this.pc?.close(); } catch { /* */ }
    this.dc = null; this.pc = null;
  }

  close(): void { this.reset(); }
}
